import { default as zod } from 'zod';
import type { ProductEnvironment, UnicornApi } from '@cld/unicorn-api';
import type { CloudinaryFetchClient, ConsoleLogger, GetAuthenticationToken, LocationApi } from '../state/types.js';

const redirectResponseSchema = zod.object({
  redirect_url: zod.string(),
});

export const createCloudinaryFetchClient =
  ({
    fetchFunction,
    getAuthenticationToken,
    pageOrigin,
    productEnvironmentExternalId,
    storage,
    redirect,
    consoleLogger,
  }: {
    productEnvironmentExternalId?: ProductEnvironment['externalId'];
    fetchFunction: typeof window.fetch;
    getAuthenticationToken: GetAuthenticationToken;
    storage: Storage;
    pageOrigin: string;
    redirect: LocationApi['redirect'];
    consoleLogger: ConsoleLogger;
  }) =>
  (configuration?: Parameters<UnicornApi['methods']['createFetchClient']>[0]): CloudinaryFetchClient => {
    const redirectPendingInfinitePromise = new Promise<Response>(() => undefined);
    const ignoreError = () => undefined;

    return async (requestInfo, requestInit) => {
      const unauthorizedResponseBehaviour = configuration?.unauthorizedResponseBehaviour ?? 'logout';
      const pkceEnabled = storage.getItem('cld_pkce') === 'on';
      const getTokenResult = await getAuthenticationToken();

      const response = await fetchFunction(requestInfo, {
        ...requestInit,
        // FIXME: consider removal after cookie auth is deprecated (although it might turn out to still be needed)
        credentials: 'include',
        headers: {
          ...requestInit?.headers,
          ...(getTokenResult.kind === 'success' ? { Authorization: `Bearer ${getTokenResult.token}` } : {}),
          ...(productEnvironmentExternalId
            ? {
                'X-Cld-Customer-External-Id': productEnvironmentExternalId,
              }
            : {}),
        },
      });

      if (response.status === 302) {
        await response
          .json()
          .then((data) => redirectResponseSchema.parseAsync(data))
          .then(({ redirect_url: redirectUrl }) => {
            consoleLogger.debug('general', `Found [302] response with redirect url - redirecting browser page to "${redirectUrl}"`);
            redirect(redirectUrl);

            return redirectPendingInfinitePromise;
          })
          .catch(ignoreError);
      }

      if (response.status === 401 && unauthorizedResponseBehaviour === 'logout') {
        // FIXME: check if possible to validate token expiration date and prevent triggering 401, instantly logging out
        consoleLogger.debug('general', 'Unauthorized [401] response - logging out (probably expired refresh token)');

        if (pkceEnabled) {
          redirect(`${pageOrigin}/auth/logout`);
        } else {
          redirect(`${pageOrigin}/users/logout`);
        }

        return redirectPendingInfinitePromise;
      }

      return response;
    };
  };
